import apiAccessStyle from './api-access.module.css';

import { useEffect, useState, useCallback } from 'react';
import { ButtonGroup } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { messageKeyPrefix } from '../../../utils/message.util';
import FileSaver from 'file-saver';

// Slice
import { uiActions } from '../../../stores/slice/ui';
import {
  apiAccessActions,
  fetchUserApiKeysData,
  fetchAuthResources,
  updateUserApiKeysName,
  generateUserApiKey,
  revokeUserApiKey,
} from '../../../stores/slice/apiAccess';

// Components
import MessageBox from '../../../components/MessageBox';
import InlineTitle from '../../../components/InlineTitle';
import PaginationContainer from '../../../components/PaginationContainer';
import Button from '../../../components/Button';
import ApiAccessTable from './ApiAccessTable';

// Model
import GenerateApiKeyModal from './modals/GenerateApiKeyModal';
import ApiKeyInfoModal from './modals/ApiKeyInfoModal';
import RevokeModal from './modals/RevokeModal';

const ApiAccess = () => {
  const defaultMessages = {
    danger: null,
    generateModalDanger: null,
  };

  const { t } = useTranslation();

  const [currentPage, setCurrentPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(10);

  const [messages, setMessages] = useState({ ...defaultMessages });
  const [isOpenGenerateModal, setIsOpenGenerateModal] = useState(false);
  const [isOpenDownloadModal, setIsOpenDownloadModal] = useState(false);
  const [isOpenRevokeModal, setIsOpenRevokeModal] = useState(false);

  const [isCheckedAll, setIsCheckedAll] = useState(false);
  const [revokeBtnDisable, setRevokeBtnDisable] = useState(true);

  const [modalApiKeyName, setModalApiKeyName] = useState('');
  const [modalApiKeyNameInvalid, setModalApiKeyNameInvalid] = useState(false);

  const { name: userName, id: userId } = useSelector(state => state.user.user);
  const {
    originalUserApiKeysData,
    userApiKeysData,
    apiEndpointUrl,
    modalGetApiKey,
  } = useSelector(state => state.apiAccess);

  const dispatch = useDispatch();

  const getApiAccessData = useCallback(async () => {
    try {
      dispatch(uiActions.showSpinner());
      await dispatch(fetchUserApiKeysData(userId)).unwrap();
      await dispatch(fetchAuthResources()).unwrap();
    } catch (error) {
      console.error('Failed to get data:', error);
      setMessages({
        ...messages,
        danger: error.code,
      });
    } finally {
      dispatch(uiActions.hideSpinner());
    }
  }, []);

  useEffect(() => {
    getApiAccessData();
  }, [getApiAccessData]);

  useEffect(() => {
    resetApiAccessData();
  }, [currentPage, pageLimit]);

  const resetApiAccessData = () => {
    // Reset: Table data and all checkboxes & disabled the revoke button.
    const newState = [...originalUserApiKeysData];
    dispatch(apiAccessActions.resetTableData(newState));
    resetCheckedAllDisabledRevokeBtn();
  };

  const handleTableCheckedAll = isCheckedAll => {
    setIsCheckedAll(isCheckedAll);

    const startIndex = currentPage !== 1 ? pageLimit * (currentPage - 1) : 0;
    const endIndex = pageLimit * (currentPage - 1) + pageLimit - 1;

    // The current page data checkbox checked
    const tmpTableData = userApiKeysData.map((tmpData, index) => {
      if (index >= startIndex && index <= endIndex) {
        if (tmpData.status !== 'INVALID') {
          return { ...tmpData, checked: isCheckedAll };
        } else {
          return tmpData;
        }
      } else {
        return tmpData;
      }
    });
    dispatch(apiAccessActions.changeTableCheckbox(tmpTableData));

    // Get current page data
    const currentTableData = userApiKeysData.filter((tmpData, index) => {
      if (index >= startIndex && index <= endIndex) {
        return tmpData;
      }
      return false;
    });

    // Check enable/disable revoke button
    const notInvalidData = currentTableData.filter(
      item => item.status !== 'INVALID'
    );
    const checkboxesTrue = notInvalidData.every(item => item.checked);
    setRevokeBtnDisable(checkboxesTrue);
  };

  const handleTableChecked = tmpData => {
    const startIndex = currentPage !== 1 ? pageLimit * (currentPage - 1) : 0;
    const endIndex = pageLimit * (currentPage - 1) + pageLimit - 1;

    // Change table checkbox
    const isTableData = userApiKeysData.map(item => {
      if (item.id === tmpData.id) {
        return tmpData;
      } else {
        return item;
      }
    });
    dispatch(apiAccessActions.changeTableCheckbox(isTableData));

    // Get current page data
    const currentTableData = isTableData.filter((item, index) => {
      if (index >= startIndex && index <= endIndex) {
        return item;
      }
      return false;
    });

    // Check checkboxAll
    const notInvalidData = currentTableData.filter(
      item => item.status !== 'INVALID'
    );
    const checkboxesTrue = notInvalidData.every(item => item.checked);
    setIsCheckedAll(checkboxesTrue);

    // Check enable/disable revoke button
    const hasCheckedData = isTableData.filter(data => data.checked);
    setRevokeBtnDisable(hasCheckedData.length > 0 ? false : true);
  };

  const handleEnterApiKeyNameValue = async tmpObj => {
    const param = {
      userId: userId,
      keyId: tmpObj.id,
      newApiKeyName: String(tmpObj.name).trimStart(),
    };
    try {
      dispatch(uiActions.showSpinner());
      await dispatch(updateUserApiKeysName(param)).unwrap();
      await dispatch(fetchUserApiKeysData(userId)).unwrap();
    } catch (error) {
      console.error('Failed to update the api key name:', error);
      setMessages({
        ...messages,
        danger: error.code,
      });
    } finally {
      resetCurrentPageAndPageLimit();
      resetCheckedAllDisabledRevokeBtn();
      dispatch(uiActions.hideSpinner());
    }
  };

  const handleCloseModal = type => {
    setModalApiKeyName('');
    setModalApiKeyNameInvalid(false);

    if (type === 'generate') {
      setIsOpenGenerateModal(false);
    } else if (type === 'download') {
      setIsOpenDownloadModal(false);
    }
  };

  const handleChangeModalInput = inputValue => {
    setModalApiKeyName(inputValue);
    setModalApiKeyNameInvalid(
      inputValue.length < 1 || inputValue.length > 64 ? true : false
    );
  };

  const handleClickModalGenerateBtn = async () => {
    if (modalApiKeyNameInvalid || !modalApiKeyName) return;

    setIsOpenGenerateModal(false);

    const param = {
      userId: userId,
      modalApiKeyName: modalApiKeyName,
    };

    try {
      dispatch(uiActions.showSpinner());
      await dispatch(generateUserApiKey(param)).unwrap();
      await dispatch(fetchUserApiKeysData(userId)).unwrap();
    } catch (error) {
      console.error('Failed to generate:', error);
      setMessages({
        ...messages,
        generateModalDanger: error.code,
      });
    } finally {
      resetCurrentPageAndPageLimit();
      resetCheckedAllDisabledRevokeBtn();
      dispatch(uiActions.hideSpinner());
    }

    setIsOpenDownloadModal(true);
  };

  const handleClickModalDownloadApiKey = () => {
    const rows = [`${modalApiKeyName}`, `${modalGetApiKey}`];
    const fileName = `${userName}_${modalApiKeyName}.csv`;
    const blob = new Blob([rows], { type: 'text/csv' });
    FileSaver.saveAs(blob, fileName);
  };

  const handleClickModalRevokeBtn = async () => {
    const revokeDataArray = userApiKeysData
      .filter(data => data.checked)
      .map(item => {
        return item.id;
      });

    const param = {
      userId,
      revokeDataArray,
    };

    try {
      dispatch(uiActions.showSpinner());
      await dispatch(revokeUserApiKey(param)).unwrap();
      await dispatch(fetchUserApiKeysData(userId)).unwrap();
    } catch (error) {
      console.error('Failed to revoke:', error);
      setMessages({
        ...messages,
        danger: error.code,
      });
    } finally {
      setIsOpenRevokeModal(false);
      resetCurrentPageAndPageLimit();
      resetCheckedAllDisabledRevokeBtn();
      dispatch(uiActions.hideSpinner());
    }
  };

  const resetCheckedAllDisabledRevokeBtn = () => {
    setIsCheckedAll(false);
    setRevokeBtnDisable(true);
  };

  const resetCurrentPageAndPageLimit = () => {
    setCurrentPage(1);
  };

  return (
    <div className="layout-container layout-container--column layout-container--fluid">
      <MessageBox
        show={!!messages.danger}
        label={t(`${messageKeyPrefix.e.s}${messages.danger}`)}
        variant="danger"
        onClose={() => setMessages({ ...messages, danger: null })}
      />

      {/* API ACCESS */}
      <InlineTitle label={t('bb7720e278')} />
      <div className="lh-lg">
        <span>{t('5be6dddecc')}</span>
        <span>
          {' '}
          <a
            href="mailto:Nuclias_ts@dlinkcorp.com"
            className={`${apiAccessStyle['mail-to-link']}`}
          >
            Nuclias_ts@dlinkcorp.com
          </a>{' '}
          {t('5c67f2500d')}
        </span>
        <div className="mt-3">{t('d91490623f')}</div>
      </div>
      <div className="form-group my-3">
        {/* API endpoint URL */}
        <div className="form-title">{t('1af172a433')}</div>
        <div className="form-field">{apiEndpointUrl}</div>
      </div>

      <ButtonGroup className="d-inline-flex mb-2">
        {/* Generate API key */}
        <Button
          label={t('608c815be4')}
          onClick={() => setIsOpenGenerateModal(true)}
        ></Button>
        {/* Revoke */}
        <Button
          label={t('64c2406bee')}
          onClick={() => setIsOpenRevokeModal(true)}
          disabled={revokeBtnDisable}
        ></Button>
      </ButtonGroup>

      <ApiAccessTable
        userApiKeysData={userApiKeysData}
        currentPage={currentPage}
        pageLimit={pageLimit}
        isCheckedAll={isCheckedAll}
        onCheckedAll={handleTableCheckedAll}
        onTableChecked={handleTableChecked}
        onEnterApiKeyNameValue={handleEnterApiKeyNameValue}
        onResetApiAccessData={resetApiAccessData}
      />

      <PaginationContainer
        previousLabel={'dd1f775e44'}
        nextLabel={'10ac3d0425'}
        total={userApiKeysData ? userApiKeysData.length : 0}
        onPageChange={currentPageNum => setCurrentPage(currentPageNum)}
        onEntryLimitChange={currentPageNum => {
          setPageLimit(currentPageNum);
          setCurrentPage(1);
        }}
        isCurrentPageId={currentPage}
      />

      {/* Generate API key Modal */}
      <GenerateApiKeyModal
        isOpenGenerateModal={isOpenGenerateModal}
        onCloseModal={handleCloseModal}
        modalApiKeyNameInvalid={modalApiKeyNameInvalid}
        modalApiKeyName={modalApiKeyName}
        onChangeModalInput={handleChangeModalInput}
        onClickModalGenerateBtn={handleClickModalGenerateBtn}
      />

      {/* API key info & Download modal */}
      <ApiKeyInfoModal
        isOpenDownloadModal={isOpenDownloadModal}
        messages={messages}
        messageKeyPrefix={messageKeyPrefix}
        setMessages={setMessages}
        modalApiKeyName={modalApiKeyName}
        modalGetApiKey={modalGetApiKey}
        onCloseModal={handleCloseModal}
        onClickModalDownloadApiKey={handleClickModalDownloadApiKey}
      />

      {/* Revoke Modal */}
      <RevokeModal
        isOpenRevokeModal={isOpenRevokeModal}
        setIsOpenRevokeModal={setIsOpenRevokeModal}
        onClickModalRevokeBtn={handleClickModalRevokeBtn}
      />
    </div>
  );
};

export default ApiAccess;
